import React, { Fragment, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import { Box, Button, Container } from '@material-ui/core'

import {
  Page,
  ContentHeader,
  LoadingFeedback,
  AlertDialog,
  MenuButton,
  Permitted,
} from 'components'

import { InformationSection, PlanActionSection } from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const DataProcessTreatmentManage = ({ match }) => {
  const [loading, setLoading] = useState(false)
  const [definedActions, setDefinedActions] = useState(null)

  const { dataProcessTreatmentId } = match.params

  const { STATUS } = constants.dataProcessTreatment

  const classes = useStyles()
  const history = useHistory()
  const snackbar = useSnackbar()

  const { response, isLoading, refresh } = useFetch(
    service.dponet.dataProcessTreatment.get,
    {
      dataProcessTreatmentId,
    },
    [dataProcessTreatmentId],
    !!dataProcessTreatmentId,
  )

  const dataProcessTreatment = response?.data?.dataProcessTreatment
  const dataProcess = dataProcessTreatment?.dataProcess

  const { response: usersResponse, isLoading: isUsersLoading } = useFetch(
    service.dponet.dataProcessTreatment.responsibles,
  )

  const users = usersResponse?.data?.users || []

  const isTreated = dataProcessTreatment?.status === STATUS.TREATED_ID
  const isRiskAssumed = dataProcessTreatment?.status === STATUS.RISK_ASSUMED_ID
  const isInactive = dataProcessTreatment?.status === STATUS.INACTIVE_ID

  const isClose = isTreated || isRiskAssumed || isInactive

  const isSoftDeletedAction = definedActions?.status === STATUS.SOFT_DELETED_ID

  const handleDefinedActions = (actions) => setDefinedActions(actions)
  const handleClearDefinedActions = () => setDefinedActions(null)

  const handleRedirectToDataProcess = () => {
    history.push(
      reverse(routes.dataProcess.view, { dataProcessId: dataProcess?.id }),
    )
  }

  const handleRedirect = () => history.push(routes.dataProcess.treatment.all)

  const handleChangeStatus = async () => {
    try {
      setLoading(true)

      await service.dponet.dataProcessTreatment.changeStatus({
        dataProcessTreatmentId,
        status: definedActions?.status,
      })

      snackbar.open({
        message: definedActions?.success || 'Ação realizada com sucesso!',
        variant: 'success',
      })

      handleClearDefinedActions()

      if (isSoftDeletedAction) handleRedirect()
      else refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Page className={classes.root} title="Tratamento de dados">
      <Container maxWidth="lg" className={classes.container}>
        <ContentHeader title="Tratamento de dados" goBack>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRedirectToDataProcess}
            disabled={isLoading}
          >
            Ver processo
          </Button>
          <Permitted
            someTags={[
              constants.permissions.DATA_PROCESS_TREATMENT.MANAGE,
              constants.permissions.DATA_PROCESSES.CREATE,
            ]}
          >
            <MenuButton iconButtonSize="small" disabled={isLoading}>
              {isClose ? (
                <Button
                  color="primary"
                  onClick={() =>
                    handleDefinedActions({
                      status: STATUS.IN_PROGRESS_ID,
                      alert: 'Tem certeza que deseja reabrir este tratamento?',
                      success: 'Tratamento reaberto com sucesso!',
                      button: 'Reabrir',
                    })
                  }
                  disabled={isLoading || loading}
                  fullWidth
                >
                  Reabrir Tratamento
                </Button>
              ) : (
                <Button
                  color="primary"
                  onClick={() =>
                    handleDefinedActions({
                      status: STATUS.RISK_ASSUMED_ID,
                      alert:
                        'Tem certeza que deseja assumir o risco deste tratamento?',
                      success: 'Risco assumido com sucesso!',
                      button: 'Assumir Risco',
                    })
                  }
                  disabled={isLoading || loading}
                  fullWidth
                >
                  Assumir Risco
                </Button>
              )}
              <Button
                color="primary"
                onClick={() =>
                  handleDefinedActions({
                    status: STATUS.SOFT_DELETED_ID,
                    alert: 'Tem certeza que deseja remover este tratamento?',
                    success: 'Tratamento removido com sucesso!',
                    button: 'Remover',
                  })
                }
                disabled={isLoading || loading}
                fullWidth
              >
                Remover
              </Button>
            </MenuButton>
          </Permitted>
        </ContentHeader>
        <LoadingFeedback open={isLoading} />
        <Box
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(3)}
          pb={6}
        >
          <InformationSection
            dataProcessTreatment={dataProcessTreatment}
            isLoading={isLoading}
          />
          <PlanActionSection
            loading={loading}
            isLoading={isLoading}
            isUsersLoading={isUsersLoading}
            refresh={refresh}
            setLoading={setLoading}
            dataProcessTreatment={dataProcessTreatment}
            users={users}
          />

          {!!definedActions && (
            <AlertDialog
              variant="outlined"
              severity="warning"
              content={definedActions?.alert}
              open={!!definedActions}
              onClose={handleClearDefinedActions}
              loading={loading}
              dialogActions={
                <Fragment>
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={handleClearDefinedActions}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleChangeStatus}
                    variant="contained"
                    className={classes.warningButton}
                    disabled={loading}
                  >
                    {definedActions?.button}
                  </Button>
                </Fragment>
              }
            />
          )}
        </Box>
      </Container>
    </Page>
  )
}

export default DataProcessTreatmentManage
